// src/services/synthesisService.js
import { supabase } from '../lib/supabase';

/**
 * Call the synthesize-article edge function.
 * @param {{
 *   sourceUrl?: string,
 *   sourceText?: string,
 *   synthesisAngle: 'key_insight'|'agree_amplify'|'disagree'|'surprising'|'question_premise'|'freeform',
 *   freeformAngleText?: string,
 * }} args
 * @returns {Promise<{
 *   success: true, synthesis_id: string|null,
 *   linkedin: string, x_thread: string[],
 *   source_metadata: { title?: string, author?: string, domain?: string, fetched_via: 'native'|'jina'|'paste' }
 * }>}
 */
export async function synthesizeArticle(args) {
  const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
  if (sessionError || !session?.access_token) {
    throw Object.assign(new Error('Session not found'), { type: 'SESSION_MISSING' });
  }

  const response = await fetch(
    `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/synthesize-article`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        source_url: args.sourceUrl,
        source_text: args.sourceText,
        synthesis_angle: args.synthesisAngle,
        freeform_angle_text: args.freeformAngleText,
      }),
    }
  );

  const result = await response.json().catch(() => ({}));

  if (response.status === 402 || result?.error === 'limit_reached') {
    throw Object.assign(new Error(result?.message || 'Generation limit reached'), {
      type: 'LIMIT_REACHED',
      used: result?.used,
      limit: result?.limit,
      daysUntilReset: result?.daysUntilReset,
    });
  }
  if (!result?.success) {
    throw Object.assign(new Error(humanizeError(result?.error)), {
      type: result?.error || 'GENERATION_FAILED',
    });
  }
  return result;
}

function humanizeError(code) {
  switch (code) {
    case 'fetch_failed':
      return "We couldn't reach that URL. Paste the article text instead.";
    case 'too_short':
      return 'That source is too short to synthesize. Paste a longer article.';
    case 'unsupported_source':
      return 'We can only synthesize from web articles right now. Paste the text instead.';
    case 'invalid_url':
      return 'That URL looks invalid. Check it or paste the article text instead.';
    case 'missing_freeform_angle':
      return 'Please describe your angle in at least a sentence.';
    case 'generation_failed':
      return 'Generation hit a snag. Try again or change the angle.';
    case 'service_config':
      return 'Service configuration error. Please contact support.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
